import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchTeamSpaces,
  fetchTeamSpace,
  createTeamChannel,
  createTeamDm,
  markTeamSpaceRead,
  addTeamSpaceMember,
  removeTeamSpaceMember,
  updateTeamSpace,
  deleteTeamSpace,
} from "../api/client";
import { WORKSPACE_RAIL_COUNTS_KEY } from "./useWorkspaceRailCounts";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { useAuthContext } from "../context/AuthContext";

function isTransientAuthFailure(error) {
  const status = error?.response?.status ?? error?.status;
  const code = error?.response?.data?.code ?? error?.code;
  return (
    status === 503 ||
    code === "errors.auth.temporarily_unavailable" ||
    code === "errors.auth.forbidden"
  );
}

export const useTeamSpaces = () => {
  const queryClient = useQueryClient();
  const { bearerToken } = useAuthContext() || {};
  const tenantSlug = getActiveWorkspaceSlug();
  const canFetch = Boolean(bearerToken && tenantSlug);

  const listQuery = useQuery(["teamSpaces", tenantSlug || ""], () => fetchTeamSpaces(), {
    enabled: canFetch,
    staleTime: 3000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (!isTransientAuthFailure(error)) return failureCount < 1;
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  const createChannelMutation = useMutation(
    (payload) => createTeamChannel(payload),
    { onSuccess: () => queryClient.invalidateQueries(["teamSpaces"]) }
  );

  const createDmMutation = useMutation(
    (payload) => createTeamDm(payload),
    { onSuccess: () => queryClient.invalidateQueries(["teamSpaces"]) }
  );

  const markReadMutation = useMutation(
    (spaceId) => markTeamSpaceRead(spaceId),
    {
      onMutate: async (spaceId) => {
        await queryClient.cancelQueries(["teamSpaces"]);
        const previous = queryClient.getQueryData(["teamSpaces"]);
        queryClient.setQueryData(["teamSpaces"], (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((s) =>
            Number(s.id) === Number(spaceId) ? { ...s, unreadCount: 0 } : s
          );
        });
        return { previous };
      },
      onError: (_err, _spaceId, context) => {
        if (context?.previous != null) {
          queryClient.setQueryData(["teamSpaces"], context.previous);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(["teamSpaces"]);
        queryClient.invalidateQueries(WORKSPACE_RAIL_COUNTS_KEY);
      },
    }
  );

  const deleteSpaceMutation = useMutation(
    (spaceId) => deleteTeamSpace(spaceId),
    { onSuccess: () => queryClient.invalidateQueries(["teamSpaces"]) }
  );

  const spaces = Array.isArray(listQuery.data) ? listQuery.data : [];

  return {
    spaces,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,
    createChannel: createChannelMutation.mutateAsync,
    createDm: createDmMutation.mutateAsync,
    markRead: markReadMutation.mutateAsync,
    deleteSpace: deleteSpaceMutation.mutateAsync,
    isCreating: createChannelMutation.isLoading || createDmMutation.isLoading,
    isDeleting: deleteSpaceMutation.isLoading,
  };
};

export const useTeamSpaceDetails = (spaceId) => {
  return useQuery(
    ["teamSpace", spaceId],
    () => fetchTeamSpace(spaceId),
    {
      enabled: Boolean(spaceId),
      staleTime: 5000,
    }
  );
};

export const useTeamSpaceAdmin = () => {
  const queryClient = useQueryClient();

  const addMemberMutation = useMutation(
    ({ spaceId, payload }) => addTeamSpaceMember(spaceId, payload),
    {
      onSuccess: (_, { spaceId }) => {
        queryClient.invalidateQueries(["teamSpace", spaceId]);
      },
    }
  );

  const removeMemberMutation = useMutation(
    ({ spaceId, memberId }) => removeTeamSpaceMember(spaceId, memberId),
    {
      onSuccess: (_, { spaceId }) => {
        queryClient.invalidateQueries(["teamSpace", spaceId]);
        queryClient.invalidateQueries(["teamSpaces"]);
      },
    }
  );

  const updateMutation = useMutation(
    ({ spaceId, payload }) => updateTeamSpace(spaceId, payload),
    {
      onSuccess: (_, { spaceId }) => {
        queryClient.invalidateQueries(["teamSpace", spaceId]);
        queryClient.invalidateQueries(["teamSpaces"]);
      },
    }
  );

  return {
    addMember: addMemberMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    updateSpace: updateMutation.mutateAsync,
    isAddingMember: addMemberMutation.isLoading,
    isRemovingMember: removeMemberMutation.isLoading,
    isUpdating: updateMutation.isLoading,
  };
};
